import type { FormField, FormFieldType } from '../form/form-fields';
import type { FieldValue } from '../form/form-fields/field-values';

/**
 * Validation configuration object to select which fields have validation enabled (will show a validation message if
 * there is a validation error).
 *
 * It is split into `fieldSet` where some pre-defined sets are defined, and `fieldIds` which allows for more
 * fine-grained control over individual fields. Everything in `ValidationTargets` is additive, e.g. `fieldIds`
 * will be applied on top of the selected `fieldSet`.
 */
export type ValidationTargets = {
	fieldSet: ValidationFieldSet;
	fieldIds?: string[];
};

// oxlint-disable-next-line eslint/no-redeclare
export const ValidationFieldSet = {
	ALL_FIELDS: 'allFields',
	MODIFIED_FIELDS: 'modifiedFields',
	NONE: 'none',
	SECURITY_CRITICAL: 'securityCritical',
} as const;

export type ValidationFieldSet = (typeof ValidationFieldSet)[keyof typeof ValidationFieldSet];

/**
 * A set of callbacks which will be invoked for each field type when validation needs to be performed. If a
 * `Validator` isn't provided for a given type, it will fall back to the default implementation.
 */
export type Validators = {
	[formFieldType in FormFieldType]?: Validator;
};

/**
 * A `Validator` returns an array of `ValidationError` for the given `FormField`. An absence of errors
 * indicate the field has passed all validation checks.
 */
export type Validator = (
	field: FormField,
	defaultFormFieldValidators: Validators,
	currentValue?: FieldValue,
) => ValidationError[];

export const ErrorKeySet = {
	REQUIRED: 'required',
	INVALID: 'invalid',
	INVALID_REGEX: 'invalidRegex',
} as const;

export type ErrorKey = (typeof ErrorKeySet)[keyof typeof ErrorKeySet];

/**
 * An error message which describes a validation error on a field.
 * If no Intl was initialised, then the errorMessage will be empty.
 */
export type ValidationError = {
	errorKey: ErrorKey;
	errorMessage?: string;
};

/**
 * The API object to aggregate the different validation configurations.
 *
 * `validationTargets` is used to select which fields have validation enabled.
 * `validators` customises what validation logic to run for fields which have validation enabled.
 */
export type FieldValidation = {
	validationTargets?: ValidationTargets;
	validators?: Validators;
};
